/**
 * Filter Service
 *
 * Handles API calls for Global Filter Strip (GO-35).
 * Fetches organization-scoped filter options for business context filters.
 */

import { fetchWithAuth } from '../utils/api';
import type {
  FilterOption,
  FilterOptions,
} from '../components/v4/records/filter/types/filter.types';

// Re-export types for convenience
export type { FilterOption, FilterOptions };

/** Raw API response format from /v1/signals/filter-options */
interface FilterOptionsApiResponse {
  product_categories: string[];
  journey_stages: string[];
  channels: string[];
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format a value into a human-readable label.
 * Converts snake_case to Title Case.
 */
function formatLabel(value: string): string {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Transform a string array to FilterOption array.
 * Creates label from value using title case.
 */
function toFilterOptions(values: string[]): FilterOption[] {
  return values.map((value) => ({
    value,
    label: formatLabel(value),
  }));
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch filter options for the Global Filter Strip.
 *
 * Returns organization-scoped distinct values for:
 * - Product categories
 * - Journey stages
 * - Channels
 *
 * @returns Promise<FilterOptions>
 */
export async function getFilterOptions(): Promise<FilterOptions> {
  const response = (await fetchWithAuth('/v1/signals/filter-options')) as FilterOptionsApiResponse;

  return {
    productCategories: toFilterOptions(response.product_categories || []),
    journeyStages: toFilterOptions(response.journey_stages || []),
    channels: toFilterOptions(response.channels || []),
  };
}
